import { DEFAULT_THEME, MantineProvider, createTheme, mergeMantineTheme as mergeTheme, rem, } from "@mantine/core";
const customizations = createTheme({
    // Colors tweaked to be more accessible
    colors: {
        gray: [
            "#FCFCFD",
            "#F9FAFB",
            "#F2F4F7",
            "#E4E7EC",
            "#D0D5DD",
            "#98A2B3",
            "#626C81",
            "#4A5565",
            "#303D51",
            "#1D293A",
        ],
        red: [
            "#FFF6F9",
            "#FFE5EC",
            "#FFC2D1",
            "#FFA9BE",
            "#FE7595",
            "#F93160",
            "#E20237",
            "#B3012B",
            "#980024",
            "#880020",
        ],
        green: [
            "#ECFDF3",
            "#D1FADF",
            "#A6F4C5",
            "#85E99B",
            "#4AC165",
            "#23AB42",
            "#008616",
            "#016311",
            "#014F0D",
            "#004B0C",
        ],
        blue: [
            "#EFF8FF",
            "#D1E9FF",
            "#B2DDFF",
            "#8FCAF8",
            "#5C9BF1",
            "#3982E8",
            "#0065F2",
            "#0350BD",
            "#0043A0",
            "#003C8F",
        ],
        orange: [
            "#FFFCEB",
            "#FFF4D4",
            "#FFDD7D",
            "#FFCC3B",
            "#FCA906",
            "#F38A0F",
            "#CD4206",
            "#A13203",
            "#832C07",
            "#742402",
        ],
    },
    // Components' primary color
    primaryColor: "blue",
    // The shade of the primary color used in components
    // The number indicates the index of that shade in the colors array
    primaryShade: {
        light: 6,
        dark: 7,
    },
    // Fonts
    scale: 1,
    fontFamily: "Inter, sans-serif",
    lineHeights: {
        xs: "1.5",
        sm: "1.5",
        md: "1.5",
        lg: "1.5",
        xl: "1.5",
    },
    headings: {
        sizes: {
            h1: {
                fontSize: "2rem",
                lineHeight: "1.25",
            },
            h2: {
                fontSize: "1.75rem",
                lineHeight: "1.25",
            },
            h3: {
                fontSize: "1.5rem",
                lineHeight: "1.25",
            },
            h4: {
                fontSize: "1.25rem",
                lineHeight: "1.25",
            },
            h5: {
                fontSize: "1rem",
                lineHeight: "1.25",
            },
            h6: {
                fontSize: "0.875rem",
                lineHeight: "1.25",
            },
        },
    },
    // Change the cursor type for all interactive components (buttons, links, etc.)
    cursorType: "pointer",
    // Only change CSS variables here as much as possible,
    // other customizations should be done in the components themselves
    components: {
        Badge: {
            vars: (_, props) => {
                return {
                    root: {
                        "--badge-radius": rem(4),
                        "--badge-padding-x": rem(4),
                        ...(props.size === "sm" && {
                            "--badge-fz": rem(14),
                            "--badge-radius": rem(2),
                            "--badge-height": rem(18),
                        }),
                        ...(props.size === "md" && {
                            "--badge-fz": rem(14),
                            "--badge-padding-x": rem(6),
                            "--badge-height": rem(20),
                        }),
                        ...(props.size === "lg" && {
                            "--badge-fz": rem(16),
                            "--badge-padding-x": rem(8),
                            "--badge-height": rem(24),
                        }),
                        ...(props.size === "xl" && {
                            "--badge-fz": rem(18),
                            "--badge-padding-x": rem(10),
                            "--badge-height": rem(28),
                        }),
                    },
                };
            },
        },
        Button: {
            vars: (_, props) => {
                return {
                    root: {
                        "--button-padding-x": rem(16),
                        ...(props.size === "md" && { "--button-height": rem(40) }),
                    },
                };
            },
        },
        // Covers all input components (TextInput, PasswordInput, etc.)
        InputWrapper: {
            vars: (_, props) => {
                return {
                    label: {
                        ...((props.size === "sm" || props.size === "md") && {
                            "--input-label-size": rem(12),
                        }),
                    },
                    description: {
                        ...((props.size === "sm" || props.size === "md") && {
                            "--input-description-size": rem(12),
                        }),
                    },
                    error: {
                        ...((props.size === "sm" || props.size === "md") && {
                            "--input-error-size": rem(12),
                        }),
                    },
                };
            },
        },
        // Covers all input components (TextInput, PasswordInput, etc.)
        Input: {
            vars: (_, props) => {
                return {
                    wrapper: {
                        ...(props.size === "md" && { "--input-height": rem(40) }),
                        "--input-padding-inline-start": rem(8),
                        "--input-padding-inline-end": rem(8),
                        // Remove margins from input wrapper
                        // Instead the margins will be added on the labels, descriptions and errors
                        "--input-margin-bottom": "0",
                        "--input-margin-top": "0",
                    },
                };
            },
        },
        // Stepper
        Stepper: {
            vars: (_) => {
                return {
                    stepIcon: {
                        "--stepper-icon-size": rem(20),
                    },
                };
            },
        },
        //Checkbox
        Checkbox: {
            vars: () => {
                return {
                    root: {
                        "--checkbox-color": "var(--mantine-primary-color-0)",
                        "--checkbox-icon-color": "var(--mantine-primary-color-filled)",
                    },
                };
            },
        },
    },
});
const theme = mergeTheme(DEFAULT_THEME, customizations);
// Set the default classNames prefix to "flp"
// Used for generating static classes for components
// This can be customized to avoid conflicts with other libraries
const ThemeProvider = ({ classNamesPrefix = "flp", ...props }) => MantineProvider({ classNamesPrefix, ...props });
export { createTheme, mergeTheme, rem, theme, ThemeProvider };
//# sourceMappingURL=theming.js.map